import * as React from "react"
import Link from "next/link"
import type { LinkProps } from "next/link"

// Combine LinkProps with AnchorProps to support all Next.js Link props
type AnchorProps = Omit<React.ComponentPropsWithoutRef<"a">, "href"> &
  LinkProps & {
    href: string
    newTab?: boolean
    dofollow?: boolean
    sponsored?: boolean
  }

/**
 * Enhanced Anchor component that handles both internal and external links.
 *
 * Features:
 * - Uses Next.js Link for internal navigation
 * - Handles external links with appropriate rel attributes
 * - Supports all Next.js Link props
 * - Respects newTab prop for all links
 * - External links open in new tab by default
 * - Special handling for CitizenShipper links to preserve analytics tracking and SEO
 */
export const Anchor = React.memo(
  ({
    href,
    children,
    newTab = false,
    className,
    dofollow = false,
    sponsored = false,
    prefetch = false, // Default to false to avoid unnecessary prefetching
    replace,
    scroll,
    shallow,
    locale,
    passHref,
    ...props
  }: AnchorProps) => {
    // Extract rel from props if provided
    const userProvidedRel = props.rel
    const { rel: _, ...restProps } = props

    // Validate and normalize the href
    const normalizedHref = React.useMemo(() => {
      try {
        // Handle empty or invalid hrefs
        if (!href || typeof href !== "string") {
          console.warn(`Invalid href provided to Anchor component: ${href}`, {
            href,
            children,
            component: "Anchor",
            stack: new Error().stack,
          })
          return "#"
        }
        return href
      } catch (error) {
        console.warn(`Error processing href in Anchor component: ${error}`, {
          href,
          children,
          component: "Anchor",
          error,
          stack: new Error().stack,
        })
        return "#"
      }
    }, [href, children])

    // Get URL information
    const urlInfo = React.useMemo(() => {
      const isExternal =
        normalizedHref.startsWith("http") ||
        normalizedHref.startsWith("https") ||
        normalizedHref.startsWith("//") ||
        normalizedHref.startsWith("mailto:") ||
        normalizedHref.startsWith("tel:")

      let isCitizenShipper = false
      let isMailtoOrTel = false
      let hostname = ""
      let isMainOrHelpDomain = false
      let isTrustedDomain = false

      try {
        if (isExternal) {
          // Identify mailto: or tel: links
          isMailtoOrTel =
            normalizedHref.startsWith("mailto:") ||
            normalizedHref.startsWith("tel:")

          if (!isMailtoOrTel) {
            // For protocol-relative URLs, prepend https: to make URL parsing work
            const urlToCheck = normalizedHref.startsWith("//")
              ? `https:${normalizedHref}`
              : normalizedHref

            // Skip non-HTTP/HTTPS URLs
            if (urlToCheck.startsWith("http")) {
              const url = new URL(urlToCheck)
              hostname = url.hostname

              // Check if it's a CitizenShipper link
              isCitizenShipper =
                hostname === "citizenshipper.com" ||
                hostname.endsWith(".citizenshipper.com")

              // Track if it's the main domain or help subdomain (which should be dofollow)
              isMainOrHelpDomain =
                hostname === "citizenshipper.com" ||
                hostname === "help.citizenshipper.com"

              // Check for trusted domains that should not get nofollow
              isTrustedDomain =
                hostname.endsWith(".gov") ||
                hostname.endsWith(".mil") ||
                hostname === "ipata.org" ||
                hostname.endsWith(".ipata.org") ||
                hostname === "aap.org" ||
                hostname.endsWith(".aap.org") ||
                hostname === "avma.org" ||
                hostname.endsWith(".avma.org") ||
                hostname === "citizenshipper.teachable.com" ||
                hostname === "citizenshipper.zendesk.com" ||
                hostname === "citizenshipper.breezy.hr" ||
                isMainOrHelpDomain
            }
          }
        }
      } catch (error) {
        console.warn("Error parsing URL in Anchor component:", error)
        // Fallback for URL parsing errors
        isCitizenShipper = normalizedHref.includes("citizenshipper.com")
        isMainOrHelpDomain =
          normalizedHref === "https://citizenshipper.com" ||
          normalizedHref.startsWith("https://citizenshipper.com/") ||
          normalizedHref === "https://help.citizenshipper.com" ||
          normalizedHref.startsWith("https://help.citizenshipper.com/")
        // Default to false for isTrustedDomain in error cases
        isTrustedDomain = isMainOrHelpDomain
      }

      return {
        isExternal,
        isCitizenShipper,
        isMailtoOrTel,
        hostname,
        isMainOrHelpDomain: isMainOrHelpDomain || false,
        isTrustedDomain: isTrustedDomain || false,
      }
    }, [normalizedHref])

    // Determine target attribute
    const target = React.useMemo(() => {
      // For external links, open in new tab by default unless newTab is explicitly set to false
      const shouldOpenNewTab = urlInfo.isExternal ? newTab !== false : newTab
      return shouldOpenNewTab ? "_blank" : "_self"
    }, [urlInfo.isExternal, newTab])

    // Build rel attribute
    const computedRel = React.useMemo(() => {
      const relParts = new Set<string>()

      // User-provided rel values take precedence
      if (userProvidedRel) {
        userProvidedRel.split(" ").forEach((part) => {
          if (part.trim()) relParts.add(part.trim())
        })
      }

      // Handle external links
      if (urlInfo.isExternal) {
        // Skip mailto: and tel: links for external attribution
        if (!urlInfo.isMailtoOrTel) {
          // Non-CitizenShipper external links get "external" rel
          if (!urlInfo.isCitizenShipper) {
            relParts.add("external")
          }
        }

        // SEO attributes based on props
        // Never add nofollow to trusted domains
        if (!dofollow && !urlInfo.isTrustedDomain) relParts.add("nofollow")
        if (sponsored) relParts.add("sponsored")
      }

      // Security attributes for new tab links
      if (target === "_blank") {
        relParts.add("noopener")
        // Add noreferrer for non-CitizenShipper links
        if (!urlInfo.isCitizenShipper || !urlInfo.isExternal) {
          relParts.add("noreferrer")
        }
      }

      // Special case for BBB links
      if (normalizedHref.includes("bbb.org") && target === "_blank") {
        relParts.add("external")
        relParts.add("noopener")
        relParts.add("noreferrer")
      }

      return Array.from(relParts).join(" ") || undefined
    }, [
      urlInfo.isExternal,
      urlInfo.isCitizenShipper,
      urlInfo.isMailtoOrTel,
      urlInfo.isTrustedDomain,
      normalizedHref,
      userProvidedRel,
      dofollow,
      sponsored,
      target,
    ])

    // Prepare props based on whether a class was provided
    const style = className
      ? undefined
      : {
          color: "var(--bs-primary, #2268DE)",
          textDecoration: "none",
          backgroundColor: "transparent",
        }

    // Common props for both components
    const commonProps = {
      // Always add hover:underline even when custom className is provided
      className: className
        ? `${className} hover:!underline`
        : "hover:!underline",
      style,
      rel: computedRel,
      ...restProps,
    }

    // Decide which component to render
    if (urlInfo.isExternal || target === "_blank") {
      // External links or any link opening in a new tab use a regular anchor tag
      return (
        <a href={normalizedHref} target={target} {...commonProps}>
          {children}
        </a>
      )
    } else {
      // Internal links opening in the same tab use Next.js Link
      return (
        <Link
          href={normalizedHref}
          prefetch={prefetch}
          replace={replace}
          scroll={scroll}
          shallow={shallow}
          locale={locale}
          passHref={passHref}
          {...commonProps}
        >
          {children}
        </Link>
      )
    }
  },
)

// Add display name for React DevTools and error messages
Anchor.displayName = "Anchor"
